import React from 'react';

interface QuickQuestionsProps {
  questions: string[];
  onQuestionClick: (question: string) => void;
  disabled?: boolean;
  isVisible?: boolean;
  className?: string;
}

const QuickQuestions: React.FC<QuickQuestionsProps> = React.memo(({ 
  questions, 
  onQuestionClick, 
  disabled = false,
  isVisible = true,
  className = ""
}) => {
  // Don't render if no questions or not visible
  if (!isVisible || !questions || questions.length === 0) {
    return null;
  }

  // Validate questions are strings
  const validQuestions = questions.filter(q => typeof q === 'string' && q.trim().length > 0);
  
  if (validQuestions.length === 0) {
    return null;
  }

  return (
    <div className={`quick-questions ${className}`}>
      {/* Questions in a single row */}
      <div className="flex flex-wrap gap-2">
        {validQuestions.map((question, index) => (
          <button
            key={index}
            className={`
              inline-flex items-center px-3 py-2 rounded-full text-sm border border-gray-200 
              transition-all duration-200 
              ${disabled 
                ? 'bg-gray-50 text-gray-400 cursor-not-allowed' 
                : 'bg-white hover:bg-blue-50 hover:border-blue-300 hover:shadow-sm active:scale-[0.98] cursor-pointer'
              }
              ${disabled ? '' : 'group'}
            `}
            onClick={() => !disabled && onQuestionClick(question)}
            disabled={disabled}
            title={disabled ? "Wait for AI response to complete" : "Click to send this question"}
          >
            <span className={`${disabled ? 'text-gray-400' : 'text-gray-700 group-hover:text-blue-700'}`}>
              {question}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if questions array, disabled state, or visibility changes
  return (
    prevProps.disabled === nextProps.disabled &&
    prevProps.isVisible === nextProps.isVisible &&
    prevProps.className === nextProps.className &&
    prevProps.questions.length === nextProps.questions.length &&
    prevProps.questions.every((q, index) => q === nextProps.questions[index])
  );
});

export default QuickQuestions;
